import { ScrollArea } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { LibraryBig, Music } from "lucide-react";
import { useState } from "react";

import {
  getListJobsQueryKey,
  useGetCatalog,
} from "../../api/generated/endpoints/generation/generation";
import { PageColumns } from "../../ui/PageColumns";
import { TypeTabs } from "../../ui/TypeTabs";
import { SoundsComposer } from "../../platform/sounds/composer/SoundsComposer";
import { SoundGenerations } from "./SoundGenerations";
import { NO_SOUNDS_FILTER, SoundsLibrary, type SoundsFilter } from "./SoundsLibrary";
import classes from "./sounds.module.css";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/sounds";

/**
 * Live's Sounds page (capture `design-ref/sound/`, spec S2): the brand's audio as the Library tab
 * and the audio generations as My Generations, with live's composer (sound effects,
 * text-to-speech, music) floating over both (#235). A start shows My Generations.
 */
export function SoundsScreen() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const search = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: "/teams/$teamId/brands/$brandId/sounds" });
  const view = search.view ?? "library";
  const catalog = useGetCatalog().data;
  const queryClient = useQueryClient();
  // Live keeps the library's search, chip and order across the page's tabs.
  const [filter, setFilter] = useState<SoundsFilter>(NO_SOUNDS_FILTER);
  const show = (next: string) =>
    void navigate({
      search: { view: next === "generations" ? next : undefined },
      replace: true,
    });
  return (
    <PageColumns
      scrollable={false}
      bottom={
        catalog === undefined ? null : (
          <SoundsComposer
            catalog={catalog}
            onStarted={() => {
              void queryClient.invalidateQueries({ queryKey: getListJobsQueryKey(teamId) });
              show("generations");
            }}
          />
        )
      }
      top={
        <TypeTabs
          label="Sounds"
          value={view}
          onChange={show}
          tabs={[
            { value: "library", label: "Library", icon: <LibraryBig size={16} /> },
            { value: "generations", label: "My Generations", icon: <Music size={16} /> },
          ]}
        />
      }
    >
      {view === "library" ? (
        <SoundsLibrary
          teamId={teamId}
          brandId={brandId}
          filter={filter}
          onFilter={(change) => setFilter((current) => ({ ...current, ...change }))}
        />
      ) : (
        <ScrollArea type="auto" offsetScrollbars className={classes.fill}>
          <SoundGenerations teamId={teamId} brandId={brandId} />
        </ScrollArea>
      )}
    </PageColumns>
  );
}
